import { DateOnly } from '@if/core/date'
import DeleteIcon from '@mui/icons-material/Delete'
import { IconButton, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import DenseTable from '@src/components/Common/Table/DenseTable'
import { JSX, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import EditPaymentTableRow from './EditPaymentTableRow'
import { EditPaymentSchemaType } from './EditPayments-schema'

interface EditPaymentTableProps {
  payments: EditPaymentSchemaType[]
  onCriticalChange: (index: number, sortPayments?: boolean) => void
  onUncriticalChange: (index: number, sortPayments?: boolean) => void
  onDelete: (index: number, payment: EditPaymentSchemaType) => void
  onRestore: (index: number, payment: EditPaymentSchemaType) => void
  paymentMethodOptions: JSX.Element[]
  onDeleteAll: () => void
  disabled?: boolean
  loanActivationDate: DateOnly
}

const EditPaymentTable = ({
  payments,
  onDelete,
  onRestore,
  onCriticalChange,
  onUncriticalChange,
  paymentMethodOptions,
  onDeleteAll,
  disabled = false,
  loanActivationDate,
}: EditPaymentTableProps) => {
  const { t } = useTranslation()

  const paymentRows = useMemo(() => {
    return payments.map((payment, index) => (
      <EditPaymentTableRow
        key={payment.id}
        index={index}
        onDelete={onDelete}
        onRestore={onRestore}
        paymentMethodOptions={paymentMethodOptions}
        onCriticalChange={onCriticalChange}
        onUncriticalChange={onUncriticalChange}
        disabled={disabled}
        loanActivationDate={loanActivationDate}
      />
    ))
  }, [
    payments,
    onDelete,
    onRestore,
    paymentMethodOptions,
    onCriticalChange,
    onUncriticalChange,
    disabled,
    loanActivationDate,
  ])

  return (
    <DenseTable stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell align="center" sx={{ minWidth: 60 }}>
            {t('loan.payment.editDialog.paymentNumber')}
          </TableCell>
          <TableCell sx={{ minWidth: 140 }}>{t('common.date')}</TableCell>
          <TableCell sx={{ minWidth: 120 }}>{t('loan.payment.amount')}</TableCell>
          <TableCell sx={{ minWidth: 120 }}>{t('loan.payment.pmtModeTitle')}</TableCell>
          <TableCell sx={{ minWidth: 150 }}>{t('loan.payment.description')}</TableCell>
          <TableCell align="center" style={{ width: '1%' }}>
            SMS
          </TableCell>
          <TableCell align="center" style={{ width: '1%' }}>
            <Tooltip title={t('loan.payment.deleteAll')}>
              <span>
                <IconButton onClick={onDeleteAll} size="small" color="error" disabled={disabled}>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>{paymentRows}</TableBody>
    </DenseTable>
  )
}

export default memo(EditPaymentTable)
